import { defineStore } from "pinia";
import piniaPersistConfig from "@/config/piniaPersist";
import { websiteInfo } from "@/config/index";
import { getCurrentLocale, changeLanguage } from "@/language";
import { listCustomerServices } from "@/api/modules/customerServices";
import { loadExternalAssets } from "@/utils/index";

import type { GlobalState } from "./typing";
import { getTradingSetting, getWebsiteSetting, getErrorCodesSetting } from "@/api/modules/settings";

export const useGlobalStore = defineStore({
	id: "global",
	state: (): GlobalState => ({
		language: getCurrentLocale(),
		theme: "light",
		website: {} as GlobalState["website"],
		trading: {} as GlobalState["trading"],
		customerServices: [] as GlobalState["customerServices"],
		withdrawParams: {} as GlobalState["withdrawParams"],
		errorCodes: [],
		isTargetBlackService: false
	}),
	getters: {
		languageGet: state => state.language,
		themeGet: state => state.theme,
		websiteGet: state => state.website,
		tradingGet: state => state.trading,
		customerServicesGet: state => state.customerServices,
		withdrawParamsGet: state => state.withdrawParams,
		errorCodesGet: state => state.errorCodes,
		isTargetBlackServiceGet: state => state.isTargetBlackService
	},
	actions: {
		/**
		 * 修改语言
		 */
		setLanguage(language: GlobalState["language"]) {
			this.language = language;
			changeLanguage(language);
		},
		setTheme(theme: GlobalState["theme"]) {
			this.theme = theme;
		},
		async setWebsite() {
			const { data } = await getWebsiteSetting();
			const origin: GlobalState["website"] = {
				...data?.row,
				name: data?.row?.name || websiteInfo.name,
				copyright: data?.row?.copyright ?? "",
				currencyUnit: data?.row?.currencyUnit || websiteInfo.currencyUnit,
				logo: data?.row?.logo || websiteInfo.logo,
				backgroundImage: websiteInfo.backgroundImage,
				popUpImage: data?.row?.popUpImage || websiteInfo.popUpImage
			};

			this.website = origin;

			if (origin?.customServiceScript) {
				loadExternalAssets({ scripts: [origin.customServiceScript], styles: [origin?.customServiceCss ?? ""] });
			}

			return data;
		},
		async setTrading() {
			const { data } = await getTradingSetting();
			const origin: GlobalState["trading"] = {
				...data?.row,
				submitTaskDelay: data?.row?.submitTaskDelay || 0,
				serviceTimeRange: data?.row?.serviceTimeRange || ["09:00", "22:00"]
			};

			this.trading = origin;
		},
		async setCustomerServices() {
			const { data } = await listCustomerServices({ enabled: 1, "order.field": "seq", "order.direction": "ASC" });
			this.customerServices = data?.rows ?? [];
			return this.customerServices;
		},
		setWithdrawParams(val: GlobalState["withdrawParams"]) {
			this.withdrawParams = val;
		},
		async setErrorCodes() {
			const { data } = await getErrorCodesSetting();
			this.errorCodes = data?.row?.codes ?? [];
			return this.errorCodes;
		},
		setIsTargetBlackService(val: GlobalState["isTargetBlackService"]) {
			this.isTargetBlackService = val;
		}
	},
	persist: piniaPersistConfig("global", [
		"language",
		"theme",
		"website",
		"trading",
		"customerServices",
		"withdrawParams"
	])
});
